import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { Box, Snackbar, Spinner, Text } from "@origin-digital/ods-core";
import { useFeedContext } from "src/context/Feed";
import { PremisesTableRow, SortByEnum, SortDirectionEnum } from "./types";
import {
  mapPremisesTableRowKeyToSortByEnum,
  normaliseIdColumnValue,
  updateToDiscard,
} from "./helpers";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RequestStatusAssignedIcon,
  RequestStatusCancelledIcon,
  RequestStatusCompletedIcon,
  RequestStatusDraftIcon,
  RequestStatusFinishedIcon,
  RequestStatusInProgressIcon,
  RequestStatusPausedIcon,
} from "./Icons";
import { ConnectionTypeIconWithHoverTip } from "../Icons";
import { usePermissions } from "src/hooks/usePermissions";
import { useTheme } from "src/hooks";
import { MessageIcon } from "./MessageIcon";
import { usePremisesNavigation } from "./hooks";
import { AccountAlreadyClosedDialogProvider } from "./AccountAlreadyClosedDialog";
import { PremisesContextMenu } from "./PremisesContextMenu";
import { DuplicateRequestDialogProvider } from "./DuplicateRequestDialog";
import { CloseTransferWarningDialogProvider } from "src/components/Form/CloseTransfer";
import { DeleteDraftDialogProvider } from "src/components/Drafts/";
import { BulkDuplicateDialogProvider } from "./BulkDuplicateDialog";
import { UpdateEligibilityDialogProvider } from "./UpdateEligibilityDialog";

const StyledTableCell = styled(TableCell)(() => ({
  textAlign: "left",
  verticalAlign: "middle",
}));

const StyledTableHeadCell = styled(StyledTableCell)(() => ({
  backgroundColor: "#f8f8f8",
}));

const StyledTableRow = styled(TableRow)(() => ({
  cursor: "pointer",
  height: 64,
}));

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "pause":
      return <RequestStatusPausedIcon />;
    case "connector":
      return <RequestStatusCompletedIcon variant="solid" />;
    case "pencil":
      return <RequestStatusDraftIcon />;
    case "assigned":
      return <RequestStatusAssignedIcon />;
    case "clock":
      return <RequestStatusInProgressIcon />;
    case "tick":
      return <RequestStatusFinishedIcon variant="solid" />;
    case "cancel":
      return <RequestStatusCancelledIcon variant="solid" />;
    default:
      return null;
  }
};

export type ColumnConfig = {
  id: keyof PremisesTableRow;
  label: string;
  width?: string;
  disableSorting?: boolean;
  flex?: number;
};

const columns: ColumnConfig[] = [
  { id: "connectionType", label: "Type", width: "50px" },
  {
    id: "operationalTeam",
    label: "Ops Team",
    width: "90px",
    disableSorting: true,
  },
  { id: "address", label: "Address", flex: 1 },
  { id: "id", label: "ID", width: "80px" },
  { id: "nmi", label: "NMI/MIRN/LOC ID", width: "100px", disableSorting: true },
  { id: "lodgedDate", label: "Lodged", width: "100px" },
  { id: "updatedDate", label: "Updated", width: "100px" },
  { id: "statusText", label: "Status", flex: 1 },
  { id: "hasUnreadMessage", label: "Msg", width: "30px" },
];

export const PremisesTable: React.FC = () => {
  const {
    rows,
    sortConfig,
    setSortConfig,
    setPage,
    doFetch,
    loading,
    scrollableProps,
    setScrollPosition,
  } = useFeedContext();
  const { isHomeUser } = usePermissions();
  const [displayingSnackbar, setDisplayingSnackbar] = useState(false);
  const { state: locationState } = useLocation();
  const handleSort = (key: keyof PremisesTableRow) => {
    const isAsc =
      sortConfig &&
      sortConfig.key === mapPremisesTableRowKeyToSortByEnum(key) &&
      sortConfig.direction === SortDirectionEnum.ASC;
    const sortBy =
      mapPremisesTableRowKeyToSortByEnum(key) ?? SortByEnum.REQUEST_ID;
    const sortDirection = isAsc
      ? SortDirectionEnum.DESC
      : SortDirectionEnum.ASC;
    setSortConfig({
      key: sortBy,
      direction: sortDirection,
    });
    setPage(0);
    setScrollPosition(0);
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortBy,
        direction: sortDirection,
      },
    });
  };

  const { hasViewAllRequestsPermissions } = usePermissions();

  const theme = useTheme();

  const reFetchPremises = () => {
    doFetch({
      incremental: false,
      pageToFetch: 0,
      sortConfigToFetch: {
        key: sortConfig?.key,
        direction: sortConfig?.direction,
      },
    });
  };

  useEffect(() => {
    if (
      locationState?.submitted === true &&
      (locationState?.isDigitalEic === true ||
        (locationState?.journeyId && locationState?.journeyId?.length > 0))
    ) {
      setDisplayingSnackbar(true);
    } else {
      setDisplayingSnackbar(false);
    }
  }, [locationState]);

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        maxHeight: 700,
        maxWidth: 1440,
        overflow: "auto",
        margin: "0 auto",
      }}
      onScroll={scrollableProps.onContainerScroll}
      ref={scrollableProps.containerRef}
    >
      <Table
        sx={{ minWidth: 650, tableLayout: "fixed" }}
        size="small"
        aria-label="premises table"
        stickyHeader
      >
        <TableHead sx={{ backgroundColor: "#F1F1F1" }}>
          <StyledTableRow>
            {columns
              .filter(
                (column) =>
                  hasViewAllRequestsPermissions ||
                  column.id !== "operationalTeam"
              )
              .map((column) => {
                const isCurrentCol =
                  sortConfig?.key ===
                  mapPremisesTableRowKeyToSortByEnum(column.id);
                return (
                  <StyledTableHeadCell
                    key={column.id}
                    sx={{
                      width: column.width,
                      flex: column.flex,
                      minWidth: column.flex ? "100px" : undefined,
                    }}
                  >
                    {column.disableSorting ? (
                      <Text variant="body-small" weight="medium">
                        {column.label}
                      </Text>
                    ) : (
                      <TableSortLabel
                        active={isCurrentCol}
                        direction={
                          isCurrentCol
                            ? (sortConfig.direction.toLowerCase() as
                                | "desc"
                                | "asc")
                            : "asc"
                        }
                        onClick={() => handleSort(column.id)}
                        sx={{
                          "&.Mui-active .MuiTableSortLabel-icon": {
                            color: theme.colors.critical,
                          },
                        }}
                      >
                        <Text
                          variant="body-small"
                          tone={isCurrentCol ? "critical" : undefined}
                          weight="medium"
                        >
                          {column.label}
                        </Text>
                      </TableSortLabel>
                    )}
                  </StyledTableHeadCell>
                );
              })}
            <StyledTableHeadCell align="right" sx={{ width: "40px" }} />
          </StyledTableRow>
        </TableHead>

        <TableBody>
          {rows &&
            rows.map((row, index) => (
              <EnhancedRow
                key={index}
                row={row}
                reFetchPremises={reFetchPremises}
                homeUser={isHomeUser}
                tableColumns={columns.filter(
                  (column) =>
                    column.id !== "hasUnreadMessage" &&
                    (hasViewAllRequestsPermissions ||
                      column.id !== "operationalTeam")
                )}
              />
            ))}
        </TableBody>
      </Table>
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          marginY="medium"
        >
          <Spinner />
        </Box>
      )}
      {displayingSnackbar && locationState ? (
        <Snackbar
          data-id="snackbar"
          open
          showClose
          tone="positive"
          autoHideDuration={5000}
          onClose={() => setDisplayingSnackbar(false)}
          align="left"
        >
          {locationState?.snackbarMessage
            ? locationState.snackbarMessage
            : `Request J ${locationState?.journeyId} lodged!`}
        </Snackbar>
      ) : null}
    </TableContainer>
  );
};

const Row = ({
  row,
  reFetchPremises,
  homeUser,
  tableColumns,
}: {
  row: PremisesTableRow;
  reFetchPremises: () => void;
  homeUser: boolean;
  tableColumns: ColumnConfig[];
}) => {
  const navigate = useNavigate();
  const { handleOnClick } = usePremisesNavigation(row);
  const idColumnDisplayValue = normaliseIdColumnValue(
    row.id,
    row.journeyId,
    row.distributorApplicationId
  );

  const onDeleteDraftConfirmed = () => {
    updateToDiscard(row.id, reFetchPremises);
  };

  return (
    <>
      <StyledTableRow onClick={handleOnClick}>
        {tableColumns.map((column) => (
          <StyledTableCell
            key={column.id}
            component="th"
            scope="row"
            sx={{
              width: column.width,
              flex: column.flex,
              minWidth: column.flex ? "100px" : undefined,
            }}
            onClick={(e) =>
              (column.id === "id" || column.id === "nmi") && e.stopPropagation()
            }
          >
            {column.id === "connectionType" && (
              <ConnectionTypeIconWithHoverTip
                type={row.connectionType}
                state={row.state}
                requestChannel={row.requestChannel}
              />
            )}
            {column.id === "operationalTeam" && (
              <Text variant="body-small">{row.operationalTeam}</Text>
            )}
            {column.id === "address" && (
              <Text variant="body-small">{row.address}</Text>
            )}
            {column.id === "id" && (
              <Text variant="body-small">{idColumnDisplayValue}</Text>
            )}
            {column.id === "nmi" && (
              <Text variant="body-small">{row.nmi || "-"}</Text>
            )}
            {column.id === "lodgedDate" && (
              <Text variant="body-small">{row.lodgedDate || "-"}</Text>
            )}
            {column.id === "updatedDate" && (
              <Text variant="body-small">{row.updatedDate || "-"}</Text>
            )}
            {column.id === "statusText" && (
              <Box display="flex" alignItems="center">
                <Box marginTop="xxsmall" marginRight="xsmall">
                  <StatusIcon status={row.status} />
                </Box>
                <Text
                  tone={row.status === "error" ? "critical" : "neutral"}
                  variant="body-small"
                >
                  {row.statusText}
                </Text>
              </Box>
            )}
          </StyledTableCell>
        ))}
        <StyledTableCell
          align="right"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/new-premise/${row.id}?message=true`);
          }}
        >
          <MessageIcon unread={row.hasUnreadMessage} />
        </StyledTableCell>
        <StyledTableCell padding="normal" sx={{ width: "40px" }}>
          <PremisesContextMenu
            row={row}
            homeUser={homeUser}
            onDeleteDraftConfirmed={onDeleteDraftConfirmed}
            reFetchPremises={reFetchPremises}
          />
        </StyledTableCell>
      </StyledTableRow>
    </>
  );
};

const EnhancedRow = (props: React.ComponentProps<typeof Row>) => (
  <AccountAlreadyClosedDialogProvider>
    <UpdateEligibilityDialogProvider>
      <BulkDuplicateDialogProvider>
        <DuplicateRequestDialogProvider>
          <CloseTransferWarningDialogProvider>
            <DeleteDraftDialogProvider>
              <Row {...props} />
            </DeleteDraftDialogProvider>
          </CloseTransferWarningDialogProvider>
        </DuplicateRequestDialogProvider>
      </BulkDuplicateDialogProvider>
    </UpdateEligibilityDialogProvider>
  </AccountAlreadyClosedDialogProvider>
);
